/**
 * Depo sağlayıcısı. Arayüz yalnızca `LocalRepository` tipini görür; gerçek
 * uygulama buraya enjekte edilir. Testler kendi sahtesini geçirir.
 */

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { LocalRepository, RepositoryInfo } from "@parafon/contracts/local-api";

export interface RepositoryContextValue {
  readonly repo: LocalRepository;
  readonly info: RepositoryInfo;
  /** Sahte depo mu — arayüz bunu kullanıcıya açıkça söyler. */
  readonly isFake: boolean;
  /** Verinin değiştiğini duyurur; listeler bu sayıya bakıp yeniden çeker. */
  readonly revision: number;
  readonly invalidate: () => void;
}

const RepositoryContext = createContext<RepositoryContextValue | null>(null);

export interface RepositoryProviderProps {
  readonly value: RepositoryContextValue;
  readonly children: ReactNode;
}

export function RepositoryProvider({ value, children }: RepositoryProviderProps) {
  const memo = useMemo(() => value, [value]);
  return <RepositoryContext.Provider value={memo}>{children}</RepositoryContext.Provider>;
}

export function useRepositoryContext(): RepositoryContextValue {
  const ctx = useContext(RepositoryContext);
  if (ctx === null) {
    throw new Error("useRepositoryContext yalnızca RepositoryProvider içinde kullanılabilir.");
  }
  return ctx;
}

export function useRepository(): LocalRepository {
  return useRepositoryContext().repo;
}
